// =============================================================================
// MyGrowth — Member Growth widget for Dashboard.
// Shows: referral link + code + QR, commission summary, badge progress, recommended opps.
// Data: GET /api/member/dashboard (aggregated single call).
// =============================================================================
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Copy, QrCode, Sparkles, Award, TrendingUp, ArrowRight, Users } from "lucide-react";

const RED = "#C8102E";
const GOLD = "#D4AF37";
const CHARCOAL = "#1A1A1A";

function CopyBtn({ text, label = "Copy", testid = "copy" }) {
  const [done, setDone] = useState(false);
  const onClick = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      toast.success("Copied");
      setTimeout(() => setDone(false), 1400);
    } catch {
      toast.error("Copy failed");
    }
  };
  return (
    <button onClick={onClick} data-testid={testid} className="inline-flex items-center gap-1 text-xs uppercase tracking-widest font-semibold border border-zinc-300 hover:border-zinc-900 px-2 py-1 bg-white">
      <Copy className="w-3 h-3" /> {done ? "Copied" : label}
    </button>
  );
}

export default function MyGrowth() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/member/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;
  const { referral, commissions, badges, counts, recommended } = data;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const refUrl = `${origin}/?ref=${referral?.code || ""}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(refUrl)}`;
  const earnedCount = badges.filter((b) => b.earned).length;
  const totalBadges = badges.length;
  const RM = (n) => `RM ${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="space-y-6" data-testid="my-growth-section">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="tag"><span className="w-1.5 h-1.5 inline-block" style={{ background: RED }} /> My Growth</div>
          <h2 className="font-display font-black text-2xl sm:text-3xl mt-2 tracking-tight">Your business at a glance</h2>
        </div>
      </div>

      {/* Referral + Commissions row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Referral card */}
        <div className="lg:col-span-2 bg-white border border-zinc-200 p-5 lg:p-6 flex flex-col sm:flex-row items-start gap-5" data-testid="growth-referral-card">
          <img src={qrUrl} alt="Referral QR" className="w-32 h-32 sm:w-36 sm:h-36 border border-zinc-200 bg-white" data-testid="growth-referral-qr" />
          <div className="flex-1 min-w-0">
            <div className="overline text-zinc-500">Your referral link</div>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <code className="font-mono text-sm bg-zinc-50 px-2 py-1 border border-zinc-200 break-all" data-testid="growth-referral-url">{refUrl}</code>
              <CopyBtn text={refUrl} testid="growth-copy-url" />
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2 text-center">
              <div className="border border-zinc-200 p-2">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Code</div>
                <div className="font-display font-black text-lg tracking-tight" data-testid="growth-referral-code">{referral?.code || "—"}</div>
              </div>
              <div className="border border-zinc-200 p-2">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Visits</div>
                <div className="font-display font-black text-lg" style={{ color: RED }} data-testid="growth-referral-visits">{referral?.visits ?? 0}</div>
              </div>
              <div className="border border-zinc-200 p-2">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Signups</div>
                <div className="font-display font-black text-lg" style={{ color: GOLD }} data-testid="growth-referral-signups">{referral?.signups ?? 0}</div>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`Join MIRESCO with my referral link: ${refUrl}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs font-semibold text-white px-3 py-1.5"
                style={{ background: "#25D366" }}
                data-testid="growth-referral-share-wa"
              >
                Share via WhatsApp
              </a>
              <CopyBtn text={referral?.code || ""} label="Copy code" testid="growth-copy-code" />
            </div>
          </div>
        </div>

        {/* Commissions summary */}
        <div className="bg-white border border-zinc-200 p-5" data-testid="growth-commissions-card">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-5 h-5" style={{ color: RED }} />
            <div className="font-display font-black text-lg tracking-tight">My Commission</div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">Pending</span><span className="font-display font-black text-lg" data-testid="growth-comm-pending">{RM(commissions.pending)}</span></div>
            <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">Approved</span><span className="font-display font-black text-lg" style={{ color: GOLD }} data-testid="growth-comm-approved">{RM(commissions.approved)}</span></div>
            <div className="flex items-center justify-between border-t border-zinc-200 pt-2 mt-2"><span className="text-xs uppercase tracking-widest font-semibold" style={{ color: RED }}>Paid</span><span className="font-display font-black text-xl" style={{ color: RED }} data-testid="growth-comm-paid">{RM(commissions.paid)}</span></div>
          </div>
          <div className="mt-4 text-[10px] uppercase tracking-widest text-zinc-500">Full history in Applications & Payments →</div>
        </div>
      </div>

      {/* Quick counts */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="growth-counts">
        <div className="border border-zinc-200 p-3 bg-white">
          <div className="overline text-zinc-500">My Leads</div>
          <div className="font-display font-black text-2xl mt-1">{counts.my_leads}</div>
        </div>
        <div className="border border-zinc-200 p-3 bg-white">
          <div className="overline text-zinc-500">Applications</div>
          <div className="font-display font-black text-2xl mt-1">{counts.my_applications}</div>
        </div>
        <div className="border border-zinc-200 p-3 bg-white">
          <div className="overline text-zinc-500">Promos</div>
          <div className="font-display font-black text-2xl mt-1">{counts.my_promos}</div>
        </div>
        <div className="border border-zinc-200 p-3 bg-white">
          <div className="overline text-zinc-500">Ref. Signups</div>
          <div className="font-display font-black text-2xl mt-1" style={{ color: GOLD }}>{referral?.signups ?? 0}</div>
        </div>
        <div className="border border-zinc-200 p-3 bg-white">
          <div className="overline text-zinc-500">Badges</div>
          <div className="font-display font-black text-2xl mt-1" style={{ color: RED }}>{earnedCount}/{totalBadges}</div>
        </div>
      </div>

      {/* Badges strip */}
      <div className="bg-white border border-zinc-200 p-5" data-testid="growth-badges-card">
        <div className="flex items-center gap-2 mb-3">
          <Award className="w-5 h-5" style={{ color: GOLD }} />
          <div className="font-display font-black text-lg tracking-tight">Achievements</div>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-2">
          {badges.map((b) => (
            <div
              key={b.slug}
              title={`${b.name} — ${b.description || ""}`}
              className={`aspect-square border flex flex-col items-center justify-center p-2 text-center ${b.earned ? "border-amber-400 bg-amber-50" : "border-zinc-200 bg-zinc-50 opacity-50"}`}
              data-testid={`growth-badge-${b.slug}`}
            >
              <div className="text-2xl leading-none">{b.emoji || "🏅"}</div>
              <div className="text-[9px] uppercase tracking-widest font-bold mt-1 leading-tight">{b.name}</div>
              <div className="text-[9px] text-zinc-500">{b.tier}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Recommended */}
      {recommended?.length > 0 && (
        <div className="bg-white border border-zinc-200" data-testid="growth-recommended-card">
          <div className="p-4 border-b border-zinc-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5" style={{ color: RED }} />
              <div className="font-display font-black text-lg tracking-tight">Recommended For You</div>
            </div>
            <Link to="/opportunities-directory" className="text-xs font-semibold hover:opacity-80 inline-flex items-center gap-1" style={{ color: RED }} data-testid="growth-rec-see-all">
              See all <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-zinc-200">
            {recommended.map((o) => (
              <Link
                key={o.opportunity_id}
                to={`/opportunity/${o.slug}`}
                className="p-4 hover:bg-zinc-50 transition-colors"
                data-testid={`growth-rec-card-${o.slug}`}
              >
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{o.brand || o.category_slug}</div>
                <div className="mt-1 font-display font-black text-base tracking-tight leading-tight">{o.title}</div>
                {o.tagline && <div className="mt-1 text-xs text-zinc-600 line-clamp-2">{o.tagline}</div>}
                <div className="mt-2 text-xs font-semibold" style={{ color: RED }}>{o.investment_required || "See details →"}</div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
